/**
 * <FlowOutcome> — ce qu'une étape de parcours annonce quand elle ne demande rien (molecule).
 *
 * Trois usages, une seule forme : « trop tard pour décaler », « contactez votre tuteur »,
 * « c'est fait ». Une pastille, un message, et les sorties passées en `children` — jamais
 * d'impasse, il y a toujours au moins un bouton dessous.
 *
 * Les mots restent chez le parcours qui les prononce : ce composant ne porte aucune copie.
 */
import React from "react"

import { Stack } from "../../atoms/containers/Stack"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"

export type FlowOutcomeTone = "neutral" | "success" | "warning"

const TONE: Record<
    FlowOutcomeTone,
    { color: "primary-soft" | "successLight" | "warningLight"; ink: "primary" | "success-strong" | "warning-strong" }
> = {
    neutral: { color: "primary-soft", ink: "primary" },
    success: { color: "successLight", ink: "success-strong" },
    warning: { color: "warningLight", ink: "warning-strong" },
}

export type FlowOutcomeProps = {
    iconName: IconName
    tone?: FlowOutcomeTone
    message: string
    /** Deuxième ligne, plus discrète — « Vous pouvez encore prévenir Camille. » */
    detail?: string
    /** Les sorties : un ou deux `<Button>`. */
    children?: React.ReactNode
    /**
     * Centrer la pastille et le texte — `false` par défaut (une étape de feuille lit à gauche).
     * Une page d'arrivée, elle, n'a que ce bloc à l'écran : il se pose au milieu.
     */
    center?: boolean
    /**
     * Le message est le titre de la page d'arrivée (« C'est fait ») : gros et gras, à l'encre forte.
     * `false` par défaut — le message d'une étape se lit comme une phrase.
     */
    heading?: boolean
    testID?: string
}

export function FlowOutcome(props: FlowOutcomeProps) {
    const { iconName, tone = "neutral", message, detail, children, center = false, heading = false, testID } = props
    const { color, ink } = TONE[tone]

    return (
        <Stack gap="4" testID={testID} align={center ? "center" : "stretch"}>
            <Stack gap="3" align={center ? "center" : "stretch"}>
                <IconBubble iconName={iconName} size={48} color={color} iconColor={ink} />
                {heading ? (
                    <Text size="text-xl" weight="extrabold" color="text-strong" center={center}>
                        {message}
                    </Text>
                ) : (
                    <Text size="text-lg" center={center}>
                        {message}
                    </Text>
                )}
                {detail != null && detail !== "" && (
                    <Text size="text-sm" color="text-muted" center={center}>
                        {detail}
                    </Text>
                )}
            </Stack>
            {children}
        </Stack>
    )
}
